import React, { useMemo, useState } from 'react'
import { com, kotlin } from '@eidu/form'
import { Paper, Stack } from '@mui/material'
import EntityTypeField from './EntityTypeField'
import PathField, { RenderablePath } from './PathField'
import getEntityPathLabel, { transformEntityPath } from './EntityPathUtils'
import EntityType = com.eidu.sharedlib.entity.type.EntityType
import EntityTypeId = com.eidu.sharedlib.entity.type.EntityTypeId
import KtList = kotlin.collections.KtList
import findPathsBetweenTypes = com.eidu.sharedlib.entity.path.findPathsBetweenTypes
import EntityPath = com.eidu.sharedlib.entity.path.EntityPath

export type EntityPathPermissionProps = {
  fromEntityTypeId: EntityTypeId | undefined
  preselectedPath?: EntityPath
  entityTypes: readonly EntityType[]
  onChange: (path: EntityPath | undefined) => void
}

const EntityPathPermissionContent: React.FC<EntityPathPermissionProps> = ({
  fromEntityTypeId,
  preselectedPath,
  entityTypes,
  onChange,
}) => {
  const [selectedEntityType, setSelectedEntityType] = useState<EntityType>(
    preselectedPath
      ? entityTypes.find((type) => type.id.equals(preselectedPath.toType)) ?? entityTypes[0]
      : entityTypes[0]
  )
  const onChangeHandler = (value: EntityType) => {
    setSelectedEntityType(value)
  }
  const paths = useMemo(
    () =>
      fromEntityTypeId
        ? findPathsBetweenTypes(
            KtList.fromJsArray(entityTypes),
            fromEntityTypeId,
            selectedEntityType.id
          ).asJsReadonlyArrayView()
        : [],
    [entityTypes, fromEntityTypeId, selectedEntityType.id]
  )

  const renderablePaths: RenderablePath[] = useMemo(
    () =>
      paths
        .slice()
        .sort((a, b) => a.edges.asJsReadonlyArrayView().length - b.edges.asJsReadonlyArrayView().length)
        .map((path) => ({
          entityPath: path,
          entityPathLabel: getEntityPathLabel(entityTypes, path),
          entityPathNodes: transformEntityPath(entityTypes, path),
        })),
    [paths, entityTypes]
  )

  return (
    <Paper variant="outlined">
      <Stack direction="column" spacing={1} sx={{ borderRadius: 1, padding: 2 }}>
        <EntityTypeField
          label="Target"
          entityTypeId={selectedEntityType.id}
          types={entityTypes}
          onChange={onChangeHandler}
        />
        <PathField paths={renderablePaths} selectedPath={preselectedPath} onChange={onChange} />
      </Stack>
    </Paper>
  )
}

export default EntityPathPermissionContent
